import { FC, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RecordingState } from './RecordingState';
import { mediaMinWidth } from '../../theme/utils';

const INTRO_PROMPTS = [
    { time: 0, text: "Who are you caring for? What's going on with them right now?" },
    { time: 20, text: "What's been the hardest part lately?" },
    { time: 45, text: 'Is there anything coming up you need help staying on top of?' },
];

const useStyles = createUseStyles({
    '@keyframes introPromptFadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 }
    },
    container: {
        width: '100%',
        textAlign: 'center',
        padding: '0 16px',

        [mediaMinWidth.md]: {
            width: '65%'
        }
    },
    label: {
        fontSize: 14,
        fontWeight: 600,
        color: 'rgba(22, 166, 217, 1)',
        marginBottom: 12
    },
    prompt: {
        fontSize: 16,
        lineHeight: 1.5,
        color: 'var(--ion-color-dark)',
        fontStyle: 'italic',
        animation: '$introPromptFadeIn 0.5s ease-in'
    }
});

export const IntroPrompts: FC = () => {
    const classes = useStyles();

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (recordingState.isRecording() && !recordingState.isPaused()) {
            intervalRef.current = setInterval(() => {
                setElapsedSeconds(prev => prev + 1);
            }, 1000);
        } else if (recordingState.isPaused() && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [recordingState, recordingState.state.isPaused, recordingState.state.isRecording]);

    // Find the current prompt based on elapsed time
    let currentPrompt = INTRO_PROMPTS[0];
    for (let i = INTRO_PROMPTS.length - 1; i >= 0; i--) {
        if (elapsedSeconds >= INTRO_PROMPTS[i].time) {
            currentPrompt = INTRO_PROMPTS[i];
            break;
        }
    }

    return (
        <div className={classes.container} data-id="intro-prompts">
            <div className={classes.label}>Bea is listening</div>
            <div key={currentPrompt.time} className={classes.prompt}>
                {currentPrompt.text}
            </div>
        </div>
    );
};
